import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { cronKeys } from '~/lib/query-keys';
import { usePaginatedList } from '~/hooks/use-paginated-list';
import type { CronRunStatus } from '~/hooks/queries/use-cron-runs';
import { formatDateTime } from '~/lib/local-datetime';

export interface CronSchedule {
  intervalMs: number;
  // Raw free-text the user originally typed to build this schedule (e.g.
  // "every day at 5pm" or "daily at 17:00"). Kept only so the edit form can
  // re-populate its free-text field with what the user last typed — NEVER
  // read for display formatting, since it's an unnormalized echo of
  // whatever phrasing/time-format they happened to use (see formatSchedule).
  cronExpression?: string;
  timeOfDay?: string;
  daysOfWeek?: number[];
  // >1 when phased to run every Nth occurrence of a single weekday (e.g.
  // "every 2 weeks on Friday"). Absent/1 means every matching weekday.
  weekInterval?: number;
  anchorAt?: string;
  // Calendar (month-aware) schedules (FRONT-551).
  dayOfMonth?: number;
  monthInterval?: number;
  nthWeekday?: number; // 1–4 = first–fourth, -1 = last (with a single daysOfWeek entry)
  once?: boolean;
  runAt?: string;
  /** IANA zone `timeOfDay` is interpreted in (FRONT-742); absent means UTC. */
  timezone?: string;
}

/**
 * The job's action, exactly as the API returns it — nested under `task`.
 * Mirrors the backend `CronJob['task']` shape (src/cron/manager.ts).
 * Note `toolArgs` is a parsed object here, not a JSON string. The earlier
 * flat top-level fields (type/prompt/agentId/tool/toolArgs) were never sent
 * by the API, which is what made the edit form open blank (FRONT-508).
 */
export interface CronTask {
  type: 'agent-prompt' | 'tool-call' | 'health-check' | 'reflect';
  agentId?: string;
  prompt?: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  /** FRONT-2170: agent-prompt only — the job's own model; absent = Default. */
  model?: string;
}

/**
 * A reflect schedule is a system-seeded cron whose task runs the `reflect`
 * skill (reflect-1..5, one per daily reflection time). They're shared and
 * ownerless, so a tenant can't pause/edit/delete them — reflection is turned
 * off per-user via the reflection setting instead. Detected the same way the
 * backend run-now route does (src/cron/routes.ts). Kept as a helper so the
 * list and detail views agree on what "a reflect schedule" is.
 */
export function isReflectCron(cron: Pick<CronJob, 'system' | 'task'>): boolean {
  return Boolean(
    cron.system &&
      cron.task?.type === 'tool-call' &&
      cron.task.toolName === 'run_skill' &&
      (cron.task.toolArgs?.skill as string | undefined) === 'reflect',
  );
}

export interface CronJob {
  id: string;
  name: string;
  /** On the wire since the row mapper (`rowToCronJob`); searched by the palette (FRONT-2436). */
  description?: string;
  schedule: CronSchedule;
  task?: CronTask;
  enabled: boolean;
  runCount?: number;
  lastRunAt?: string;
  lastResult?: string;
  // The latest run's real status (FRONT-809). Prefer this over sniffing
  // `lastResult` text for "error" — see cron-list.tsx's hasError.
  lastStatus?: CronRunStatus;
  nextRunAt?: string;
  // No owning user — a seeded system cron (e.g. reflect) that authenticates
  // per user at fire time. Surfaced so the list can skip the re-auth UI.
  system?: boolean;
  /** FRONT-2780: set once a one-time job reached its final outcome ("Done"). */
  completedAt?: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const NTH_LABELS: Record<string, string> = { '1': 'First', '2': 'Second', '3': 'Third', '4': 'Fourth', '-1': 'Last' };

/** Format a day number as an English ordinal: 1 → "1st", 22 → "22nd", 31 → "31st". */
function ordinalDay(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0]}`;
}

/**
 * Render a `CronSchedule` as a short human-readable string, ALWAYS built
 * from the structured fields (`timeOfDay`, `daysOfWeek`, `intervalMs`, …) —
 * never from `schedule.cronExpression`, which is just an unnormalized echo
 * of whatever free text the user originally typed. Two schedules with the
 * exact same meaning could otherwise render differently ("daily at 17:21"
 * vs. "every day at 5:21pm") purely based on how each was phrased at
 * creation time. `timeOfDay` is already a normalized 24h "HH:MM" string on
 * every schedule the backend parses, so no time-format conversion is
 * needed here — just don't bypass it in favor of the raw text.
 */
export function formatSchedule(schedule: CronSchedule | string | null | undefined): string {
  if (!schedule) return '—';
  if (typeof schedule === 'string') return schedule;
  if (schedule.once && schedule.runAt) {
    return `Once at ${formatDateTime(schedule.runAt)}`;
  }
  // Phased biweekly+ schedules ("every 2 weeks on Friday") carry a single
  // weekday in daysOfWeek plus weekInterval — render as one phrase rather
  // than falling into the generic interval/day-list branches below, which
  // don't know how to express the "every Nth week" cadence at all.
  if (schedule.weekInterval && schedule.weekInterval > 1 && schedule.daysOfWeek?.length === 1) {
    const day = DAY_NAMES[schedule.daysOfWeek[0]] ?? schedule.daysOfWeek[0];
    const time = schedule.timeOfDay ? ` at ${schedule.timeOfDay}` : '';
    return `Every ${schedule.weekInterval} weeks on ${day}${time}`;
  }

  // Calendar (month-aware) schedules (FRONT-551).
  if (schedule.nthWeekday != null && schedule.daysOfWeek?.length) {
    const ord = NTH_LABELS[String(schedule.nthWeekday)] ?? `#${schedule.nthWeekday}`;
    const day = DAY_NAMES_FULL[schedule.daysOfWeek[0]] ?? schedule.daysOfWeek[0];
    const at = schedule.timeOfDay ? ` at ${schedule.timeOfDay}` : '';
    return `${ord} ${day} of the month${at}`;
  }
  if (schedule.dayOfMonth != null) {
    const cadence = schedule.monthInterval && schedule.monthInterval > 1 ? `Every ${schedule.monthInterval} months` : 'Monthly';
    const at = schedule.timeOfDay ? ` at ${schedule.timeOfDay}` : '';
    return `${cadence} on the ${ordinalDay(schedule.dayOfMonth)}${at}`;
  }

  const parts: string[] = [];
  if (schedule.intervalMs) {
    const ms = schedule.intervalMs;
    if (ms >= 86_400_000) {
      const days = Math.round(ms / 86_400_000);
      parts.push(days === 1 ? 'Daily' : `Every ${days}d`);
    } else if (ms >= 3_600_000) {
      const hours = Math.round(ms / 3_600_000);
      parts.push(hours === 1 ? 'Hourly' : `Every ${hours}h`);
    } else if (ms >= 60_000) {
      const mins = Math.round(ms / 60_000);
      parts.push(mins === 1 ? 'Every minute' : `Every ${mins}m`);
    } else {
      parts.push(`Every ${ms}ms`);
    }
  }
  if (schedule.timeOfDay) parts.push(`at ${schedule.timeOfDay}`);
  if (schedule.daysOfWeek?.length) {
    parts.push(`on ${schedule.daysOfWeek.map((d) => DAY_NAMES[d] ?? d).join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' ') : '—';
}

/**
 * Paginated cron jobs (ADR-0023). The backend slices the upstream Restate
 * list at the HTTP edge, returning the shared `Paginated<CronJob>` envelope,
 * ordered active, needs re-auth, paused, then the system reflect jobs
 * (`src/cron/list-order.ts`). Callers receive a flat items array plus the
 * infinite-query controls (the Schedule page's "Load more").
 *
 * `query` is the Schedule page's search box, matched server-side over the
 * whole list before the page slice (`src/cron/list-order.ts`). A blank query
 * reads the same cache entry every other caller does, so the page's
 * unfiltered read and theirs stay one request. The previous result stays on
 * screen while a new query loads, so typing never flashes the skeleton.
 */
export function useCrons(query?: string, options?: { enabled?: boolean }) {
  const q = query?.trim() ?? '';
  const list = usePaginatedList<CronJob>(cronKeys.search(q), '/api/crons', {
    // FRONT-2436: the command palette reads this list only while searching.
    enabled: options?.enabled,
    // The server's maximum page. The order is active/updated-first and
    // `updated_at` moves every time a job fires, so an offset page 2 read a
    // minute after page 1 can skip or repeat a job. One page holds every
    // job for all but the very largest lists, which also means the detail
    // page (which looks its job up in this list) always finds it.
    pageSize: CRONS_PAGE_SIZE,
    params: q ? { q } : undefined,
    keepPreviousWhen: q ? (previousKey) => previousKey[1] === 'list' : undefined,
  });
  // Past 200 jobs a later page can still repeat one that moved; never
  // render it twice.
  const items = useMemo(() => uniqueById(list.items), [list.items]);
  return { ...list, items };
}

export const CRONS_PAGE_SIZE = 200;

export function uniqueById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

export interface ReflectionJob {
  id: string;
  name: string;
  schedule: CronSchedule;
  nextRunAt: string | null;
}

/**
 * FRONT-2439: the system reflection jobs (`reflect-1..N`) the Reflection page
 * shows as one schedule — read on their own rather than out of `useCrons`,
 * whose pages may not have reached them (they are pinned last).
 */
/**
 * FRONT-2170: the model a scheduled job runs on when it has no model of its
 * own (the workspace default, resolved the way a run resolves it), so the
 * job form and page can say "Default (<model>)".
 */
export function useCronDefaultModel() {
  return useQuery({
    queryKey: cronKeys.defaultModel(),
    queryFn: () => api.get<{ model: string }>('/api/crons/default-model'),
    select: (d) => d.model,
    staleTime: 60_000,
  });
}

export function useReflectionSchedule() {
  return useQuery({
    queryKey: cronKeys.reflection(),
    queryFn: () => api.get<{ jobs: ReflectionJob[] }>('/api/crons/reflection'),
    select: (d) => d.jobs,
  });
}
